import clsx from "clsx";
import gsap from "gsap";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { Swiper, SwiperSlide } from "swiper/react";
import { useHover } from "@uidotdev/usehooks";
import useGlobalStore from "../../../store/useGlobalStore";

export default function WorksImagesSwiper(
    {
        images = []
    }
) {
    const [rotatedImages, setRotatedImages] = useState([...images].map((v, index) => {
        return {
            ...v,
            rotate: index % 2 === 0 ? gsap.utils.random(0, 8) : gsap.utils.random(-8, -0)
        }
    }));

    const [currentSwipeIndex, setCurrentSwipeIndex] = useState(0);
    const [showFirstSlide, setShowFirstSlide] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);

    const swipedImages = [...rotatedImages].slice(0, currentSwipeIndex + 1);
    const notSwipedImages = [...rotatedImages].slice(currentSwipeIndex);
    const swiperRef = useRef(null);
    const [swiperContainerRef, hovering] = useHover();
    const globalStore = useGlobalStore();
    const fullscreenSwiperRef = useRef(null);

    useEffect(() => {
        if (hovering) {
            globalStore.setCursorPopupText("Zoeiter de plus près");
            globalStore.setCursorPopupActive(true);
        } else {
            globalStore.setCursorPopupActive(false);
        }
    }, [hovering]);

    function handleSwiperProgress(progress) {
        const progressLimit = swipedImages.length > 1 ? (2 / notSwipedImages.length) : (1 / (notSwipedImages.length - 1));
        const progressPercent = progress / progressLimit;
        setShowFirstSlide(swipedImages.length > 1 ? progressPercent - 0.5 < 0 : progressPercent < 0);
    }

    function slideButtonTrigger(target_index) {
        setCurrentSwipeIndex(target_index);
    }

    const isMobile = useMediaQuery(
        {
            query: "(max-width: 780px)"
        }
    )

    useEffect(()=>{
        if(fullscreenSwiperRef){
            fullscreenSwiperRef.current.slideToLoop(currentSwipeIndex);
        }
    },[currentSwipeIndex]);

    return (
        <>
            <div className="w-full h-full relative" ref={swiperContainerRef}>
                <div className="w-full h-full absolute top-0 left-0">
                    {swipedImages.map((v, index) =>
                        <div className={clsx(
                            "w-full h-full absolute top-0 left-0 py-16 pr-24 pl-8",
                            (index === swipedImages.length - 1 && showFirstSlide) && "hidden"
                        )} key={index}>
                            <img src={v.src} alt={v.alt} className="object-cover w-full h-full" style={{
                                rotate: `${v.rotate}deg`
                            }} />
                        </div>
                    )}
                </div>
                <div className='absolute top-0 left-0 w-full h-full z-10 pointer-events-none'
                    style={{
                        backgroundImage: 'linear-gradient(to right, transparent 0%, transparent 90%, white 100%)',
                    }}></div>
                <Swiper
                    key={currentSwipeIndex}
                    className="w-full h-full absolute top-0 left-0"
                    onSlideNextTransitionEnd={(swiper) => {
                        if (swipedImages.length < 2 || swiper.activeIndex > 1) {
                            setCurrentSwipeIndex(currentSwipeIndex + 1);
                        }
                    }}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                        // Si la première slide est vide, démarre à la deuxième
                        if (swipedImages.length > 1) {
                            swiper.slideTo(1, 0);  // Démarre à la deuxième slide instantanément
                        }
                    }}
                    onProgress={(swiper) => {
                        handleSwiperProgress(swiper.progress);
                    }}
                    onSlidePrevTransitionEnd={() => {
                        setCurrentSwipeIndex(currentSwipeIndex - 1);
                    }}
                    onClick={() => {
                        setFullScreen(true);
                    }}
                >
                    {swipedImages.length > 1 && <SwiperSlide></SwiperSlide>} {/* Slide vide */}
                    {notSwipedImages.map((v, index) => (
                        <SwiperSlide className={clsx(
                            "w-full h-full py-16 pr-24 pl-8",
                            (index === 0) && (showFirstSlide ? "opacity-100" : "opacity-0")
                        )} key={index}>
                            <img src={v.src} alt={v.alt} className="w-full h-full object-cover" style={{
                                rotate: `${v.rotate}deg`
                            }} />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
            <div className="flex items-center justify-center gap-[5px] mr-24 ml-8">
                {rotatedImages.map((v, index) =>
                    <button onClick={() => {
                        slideButtonTrigger(index);
                    }} className={clsx(
                        "w-full h-[1rem] block rounded-full transition-all origin-center",
                        index === currentSwipeIndex ? "max-w-[3rem] bg-zoeil-dark-grey" : "max-w-[1rem] bg-zoeil-greyer"
                    )}>

                    </button>)}
            </div>
            <div className={clsx(
                "fixed w-full h-full z-[999] bg-[rgba(0,0,0,0.5)] top-0 left-0 flex flex-col justify-center items-center overflow-hidden py-32 transition-all",
                !fullScreen && "scale-[0]"
            )}>
                <div className="w-full h-full max-h-[685px] overflow-hidden mobile:max-h-[345px]">
                    <Swiper
                        slidesPerView={isMobile ? 1 : 1.5}
                        centeredSlides={true}
                        loop={true}
                        className="h-full"
                        onSwiper={(swiper)=>{
                            fullscreenSwiperRef.current = swiper;
                        }}>
                        {images.map(v =>
                            <SwiperSlide className="px-24 tablet:px-16 mobile:px-8">
                                <img src={v.src} alt={v.alt} className="w-full h-full object-cover" />
                            </SwiperSlide>
                        )}
                    </Swiper>
                </div>
                <button onClick={() => {
                    setFullScreen(false);
                }} className="absolute right-8 top-8 w-[2rem] aspect-square after:content-[''] after:w-full after:h-[3px] after:bg-white after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:rotate-45 before:content-[''] before:w-full before:h-[3px] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:bg-white before:-rotate-[45deg]">

                </button>
            </div>
        </>
    )
}
